import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
  getTopics,
  getTopicsBySubjectData,
  getTopicsWithSubTopics,
} from './TopicApi';
import { adminUpdateTopicSorting } from './TopicAdminApi';
import { IGroupedTopic } from '../SubTopicSlice';

export interface ITopic {
  _id: string;
  name: string;
  curriculum: string;
  grade: string;
  subject: string;
  subjectCode: string;
  subjectLevel: string;
  papers?: Array<{ name: string }>;
  sortingOrder?: number;
  isAssessment?: boolean;
}

export interface ITopicState {
  status: 'idle' | 'loading' | 'rejected';
  list: Array<ITopic>;
  topicsWithSubTopics: IGroupedTopic[];
}

const initialState: ITopicState = {
  status: 'idle',
  list: [],
  topicsWithSubTopics: [],
};

export const TopicSlice = createSlice({
  name: 'topics',
  initialState,
  reducers: {
    resetTopicList: (state) => {
      state.list = [];
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Get subject levels
      .addCase(getTopics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getTopics.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload;
      })
      .addCase(getTopics.rejected, (state) => {
        state.status = 'rejected';
      })
      // Update Topic sorting order
      .addCase(adminUpdateTopicSorting.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminUpdateTopicSorting.fulfilled, (state, action) => {
        state.status = 'idle';

        const newList = [...state.list];

        if (newList && newList.length && action.payload.length) {
          action.payload.forEach((topicSortingItem) => {
            const index = newList.findIndex(
              (topic) => topic._id === topicSortingItem.topicId
            );

            if (index > -1 && newList[index]) {
              newList[index] = {
                ...newList[index],
                sortingOrder: topicSortingItem.sortingOrder || undefined,
              };
            }
          });
        }

        state.list = newList;
      })
      .addCase(adminUpdateTopicSorting.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Topics with subTopics
      .addCase(getTopicsWithSubTopics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getTopicsWithSubTopics.fulfilled, (state, action) => {
        state.status = 'idle';
        state.topicsWithSubTopics = action.payload;
      })
      .addCase(getTopicsWithSubTopics.rejected, (state) => {
        state.status = 'rejected';
      })
      // Get topics by subject data
      .addCase(getTopicsBySubjectData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getTopicsBySubjectData.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload;
      })
      .addCase(getTopicsBySubjectData.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const { resetTopicList } = TopicSlice.actions;

export const getTopicState = (state: RootState): RootState['topic'] =>
  state.topic;

export * from './TopicAdminApi';
export * from './TopicApi';
export default TopicSlice.reducer;
